import { useState, FormEvent } from 'react';
import type { PinInputProps } from './PinInput.types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const PinInput = ({
  onSubmit,
  loading = false,
  error,
  defaultEmail = '',
  onResendPin,
  resendLoading = false,
  resendInfo,
}: PinInputProps) => {
  const [email, setEmail] = useState(defaultEmail);
  const [pin, setPin] = useState('');
  const [touched, setTouched] = useState(false);

  const emailValid = EMAIL_PATTERN.test(email.trim());
  const pinValid = /^\d{6}$/.test(pin.trim());
  const canSubmit = emailValid && pinValid && !loading;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onSubmit({ email: email.trim(), pin: pin.trim() });
  };

  const handleResend = () => {
    if (!onResendPin || resendLoading || loading || !emailValid) return;
    onResendPin(email.trim());
  };

  return (
    <div className="flex justify-center items-center px-4 min-h-screen bg-gray-50">
      <div className="p-8 w-full max-w-md bg-white rounded-lg shadow-lg">
        <h1 className="mb-2 text-2xl font-bold text-gray-900">
          Dostęp do raportu
        </h1>
        <p className="mb-6 text-sm text-gray-600">
          Wprowadź adres email oraz 6-cyfrowy PIN otrzymany w wiadomości email.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <label
              htmlFor="email-input"
              className="block mb-2 text-sm font-medium text-gray-700"
            >
              Adres email
            </label>
            <input
              id="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              autoComplete="email"
              autoFocus
              className="px-4 py-2 w-full rounded-md border border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="jan.kowalski@example.com"
              aria-invalid={touched && !emailValid ? 'true' : 'false'}
              aria-describedby={
                touched && !emailValid ? 'email-error' : undefined
              }
            />
            {touched && !emailValid && (
              <p
                id="email-error"
                className="mt-1 text-xs text-red-600"
                role="alert"
              >
                Podaj poprawny adres email.
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="pin-input"
              className="block mb-2 text-sm font-medium text-gray-700"
            >
              PIN (6 cyfr)
            </label>
            <input
              id="pin-input"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={pin}
              onChange={(e) =>
                setPin(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              disabled={loading}
              autoComplete="one-time-code"
              className="px-4 py-2 w-full text-lg tracking-[0.4em] text-center rounded-md border border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="000000"
              aria-invalid={error || (touched && !pinValid) ? 'true' : 'false'}
              aria-describedby={error ? 'pin-error' : undefined}
            />
          </div>

          {error && (
            <div
              id="pin-error"
              className="p-3 text-sm text-red-800 bg-red-50 rounded-md"
              role="alert"
            >
              {error}
            </div>
          )}

          {resendInfo && (
            <div
              className="p-3 text-sm text-emerald-800 bg-emerald-50 rounded-md"
              role="status"
            >
              {resendInfo}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2 w-full font-medium text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700 focus:outline-none disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            {loading ? 'Sprawdzanie...' : 'Wyświetl raport'}
          </button>

          {onResendPin && (
            <div className="group relative">
              <button
                type="button"
                onClick={handleResend}
                aria-disabled={resendLoading || !emailValid || loading}
                className="block w-full text-sm text-blue-600 underline aria-disabled:text-gray-400 aria-disabled:cursor-not-allowed"
                aria-describedby={!emailValid ? 'resend-pin-hint' : undefined}
              >
                {resendLoading
                  ? 'Wysyłanie...'
                  : 'Nie otrzymałem PIN-u — wyślij ponownie'}
              </button>
              {!emailValid && (
                <div
                  id="resend-pin-hint"
                  role="tooltip"
                  className="pointer-events-none absolute bottom-full left-1/2 mb-2 w-64 max-w-[90vw] -translate-x-1/2 whitespace-normal rounded-lg bg-text-primary px-3 py-1.5 text-center font-inter text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
                >
                  Najpierw wprowadź adres email powiązany z zamówieniem aby
                  zresetować PIN
                  <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-text-primary" />
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
